import { alpha, createTheme } from "@mui/material/styles";

// Brown and sage palette v1 (frozen). Source of truth:
// docs/todo/ui-redesign/designs/brown-palette-v1.json
export const brownPaletteV1 = {
  shared: {
    chrome: "#504B43",
    chromeText: "#FFF2DF",
    chromeBorder: "#6F8F72",
    brandAccent: "#EDB28C",
  },
  light: {
    background: "#F2EDE5",
    surface: "#FFFAF3",
    text: "#352F28",
    textSecondary: "#73685C",
    border: "#DED4C6",
    sage: "#6F8F72",
    accentSurface: "#E5ECDF",
    accentText: "#506B50",
  },
  dark: {
    background: "#211D19",
    surface: "#2D2722",
    text: "#F3E8D8",
    textSecondary: "#BCAE9B",
    border: "#4B4035",
    sage: "#95AD83",
    accentSurface: "#3B4834",
    accentText: "#B2C5A0",
  },
};

const getPalette = (mode) => {
  const { shared } = brownPaletteV1;
  const tokens = mode === "dark" ? brownPaletteV1.dark : brownPaletteV1.light;

  return {
    mode,
    primary: {
      main: tokens.accentText,
      contrastText: tokens.surface,
    },
    secondary: {
      main: tokens.textSecondary,
      contrastText: tokens.surface,
    },
    background: {
      default: tokens.background,
      paper: tokens.surface,
    },
    text: {
      primary: tokens.text,
      secondary: tokens.textSecondary,
    },
    divider: tokens.border,
    // Sage marks identity, emphasis, and social sections.
    sage: {
      main: tokens.sage,
      surface: tokens.accentSurface,
      text: tokens.accentText,
    },
    appShell: {
      background: shared.chrome,
      chrome: shared.chrome,
      chromeText: shared.chromeText,
      accent: shared.chromeBorder,
      brandText: shared.chromeText,
      brandAccent: shared.brandAccent,
    },
  };
};

const fontFamily = [
  "system-ui",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(",");

export const createAppTheme = (mode) => {
  const palette = getPalette(mode);
  const focusRing = {
    outline: `2px solid ${palette.sage.main}`,
    outlineOffset: "2px",
  };

  return createTheme({
    palette,
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily,
      h1: { fontSize: "1.9rem", fontWeight: 700, letterSpacing: "-0.8px", lineHeight: 1.15 },
      h2: { fontSize: "1.35rem", fontWeight: 650, letterSpacing: "-0.4px", lineHeight: 1.25 },
      h3: { fontSize: "1.2rem", fontWeight: 650, letterSpacing: "-0.4px", lineHeight: 1.25 },
      button: { textTransform: "none", fontWeight: 600 },
      overline: { fontSize: "0.6875rem", letterSpacing: "1.6px", lineHeight: 1.5 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background.default,
          },
          ":focus-visible": focusRing,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: 40,
            "&.Mui-focusVisible": focusRing,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": focusRing,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": focusRing,
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: "hover",
        },
        styleOverrides: {
          root: {
            "&.Mui-focusVisible, &:focus-visible": focusRing,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.divider,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.sage.main,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(palette.text.primary, 0.05),
            "&:hover": {
              backgroundColor: alpha(palette.text.primary, 0.08),
            },
            "&.Mui-focused": {
              backgroundColor: alpha(palette.text.primary, 0.05),
            },
            "&::after": {
              borderBottomColor: palette.sage.main,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          outlined: {
            borderColor: palette.divider,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          root: {
            borderRadius: 14,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            "&:not(.MuiBackdrop-invisible)": {
              backgroundColor: alpha(brownPaletteV1.dark.background, 0.7),
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: brownPaletteV1.shared.chrome,
            color: brownPaletteV1.shared.chromeText,
          },
          arrow: {
            color: brownPaletteV1.shared.chrome,
          },
        },
      },
    },
  });
};
